import Vue from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import applicationManageMixin from './applicationManageMixin'
import gigJobPartiesMixin from './gigJobPartiesMixin'
import startableMixin from './startableMixin'
import statusesMixin from './statusesMixin'
import waitForRefreshApplicationMixin from './waitForRefreshApplicationMixin'
import BigNumber from 'bignumber.js'
import { applyOffer, declineOffer } from '@/api/gigs/offers/offers'
import GigOffer from '@/models-ts/gigs/GigOffer'
import { googleAnalyticsV2 } from '@/servicies-ts/analytics'
import { RootState } from '@/store'
import notifiableRequest from '@/utils-ts/notifiableRequest'
import sleep from '@/utils/sleep'

export default Vue.extend({
  mixins: [applicationManageMixin, gigJobPartiesMixin, statusesMixin, startableMixin, waitForRefreshApplicationMixin],
  data() {
    return {
      accepting: false,
    }
  },
  computed: {
    ...mapState<RootState>({
      application: (state: RootState) => state.gigChat.application.value,
      decliningOffer: (state: RootState) => state.gigChat.decliningOffer,
    }),
    ...mapGetters({
      backendConfig: 'user/getConfigMap',
    }),
    hasSendOffer() {
      return this.isWorker && !this.isArchived && !this.application?.offer
    },
    appOffer() {
      return this.application.offer
    },
    declining() {
      return this.appOffer.id === this.decliningOffer
    },
  },
  methods: {
    ...mapActions({
      openModal: 'ui/openModal',
    }),
    ...mapMutations({
      updateApplication: 'gigChat/updateApplication',
      setAcceptingOffer: 'gigChat/setAcceptingOffer',
      setDecliningOffer: 'gigChat/setDecliningOffer',
    }),
    createOffer() {
      const budget = Number(this.application.budget) || 15
      const deadline = this.application.deadline > 0 ? this.application.deadline / 86400 : 0
      this.openModal({
        component: 'lx-lazy-modal',
        props: {
          factory: import(/* webpackChunkName: "job-modals" */ '@/modals/SendOffer/SendOffer.vue'),
          title: 'Send an offer',
          props: {
            applicationId: this.application.id,
            initBudget: budget,
            initDays: deadline,
            gig: this.application.gig,
            onSuccess: async () => {
              this.markApplicationAsRead()
              await this.waitRefreshApplication()
            },
          },
        },
      })
    },
    async cancelOffer() {
      try {
        const id = this.appOffer?.id
        if (!id) return

        this.setDecliningOffer(id)
        await declineOffer({ id, comment: 'Not specified' })
        if (this.appOffer?.id === id) {
          this.updateApplication({ id: this.application.id, offer: null })
        }
      } catch (e) {
        console.error('Error canceling offer:', e)
        this.openSnackbar({
          type: this.SnackTypes.FAILURE,
          text: 'Error declining offer. Please try again.',
        })
        throw e
      } finally {
        this.setDecliningOffer(null)
      }
    },
    async editOffer() {
      if (!this.appOffer) return

      this.openModal({
        component: 'lx-lazy-modal',
        props: {
          factory: import(/* webpackChunkName: "job-modals" */ '@/modals/SendOffer/SendOffer.vue'),
          title: 'Send an offer',
          props: {
            applicationId: this.application.id,
            offer: this.appOffer,
            initBudget: new BigNumber(this.appOffer.budget).toNumber(),
            initDays: this.appOffer.deadlineInDays,
            initHours: this.appOffer.timeValue,
            gig: this.application.gig,
            onSuccess: this.waitRefreshApplication,
          },
        },
      })
    },
    async acceptOffer() {
      try {
        const offer: GigOffer = this.application.offer
        this.setAcceptingOffer(offer.id)

        googleAnalyticsV2.send({
          event: 'gig_accept_click',
          gig_id: this.application.gigId,
        })

        await notifiableRequest({
          request: async () => {
            await applyOffer(offer.id)
            await sleep(this.backendConfig['CREATE_CONTRACT_BACKEND_FLOW_TIMEOUT'] || 2000)
          },
          title: 'Offer accepted',
          successText: `You have an offer for this Gig.`,
          failureText: 'Error accepting offer. Please try again.',
        })
        await this.waitRefreshApplication()
        this.startJob()
      } finally {
        this.setAcceptingOffer(null)
      }
    },
  },
})
